import { loadStripe } from '@stripe/stripe-js'

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import { getProducts, createSession, createNoCreditCardSession } from '../services/api'
import normalization from '../services/normalization'
import withNotifications from '~/components/with-notifications'
import { showErrorNotification } from '~/core/ui'
import analytics from '~/services/analytics'
import componentsConfig from '../components-config'
import { Context, defaultContext } from './context'
import appConfig from '~/app-config'
import { APP_NAMES } from '~/core/constants'

let stripe

const App = ({ publicKey, planId }) => {
  const [dialog, setDialog] = useState(null)
  const [context, setContext] = useState(defaultContext)
  const { App: RenderedApp } = componentsConfig.current
  const { normalizeProducts, normalizeForAnalytics } = normalization.current

  useEffect(async () => {
    let hasMatchingProduct
    const { products } = context

    if (planId) {
      hasMatchingProduct = products.some(p => p.id === planId)
    }

    if (hasMatchingProduct) {
      await getSession(planId)
    }
  }, [planId, context.products])

  const init = async () => {
    let products

    try {
      stripe = await loadStripe(publicKey)
      products = await getProducts()
    } catch (err) {
      return showErrorNotification('Failed to retrieve available products')
    }

    const normalizedProducts = normalizeProducts(products)
    setContext({
      ...context,
      products: normalizedProducts
    })

    analytics.trackPageView(analytics.PAGE_NAMES.PAYMENTS_PAGE, {
      prices: normalizeForAnalytics(products),
      planId: planId || null
    })
  }

  const getSessionWithStripe = async id => {
    const couponKey = context.coupon && context.coupon.couponKey
    const { sessionId } = await createSession(id, couponKey)
    const { error } = await stripe.redirectToCheckout({
      sessionId
    })

    if (error) {
      throw new Error(error)
    }
  }

  const getNoCreditCardSession = async () => {
    await createNoCreditCardSession()
    appConfig.current.goToApp()
  }

  const getSession = async id => {
    setContext({
      ...context,
      loading: true
    })

    analytics.trackEvent(analytics.EVENT_NAMES.SELECT_PRO_PLAN, undefined, {
      planId: id
    })

    const requireCreditCard = appConfig.current.name === APP_NAMES.MEET || context.products.find(p => p.id === id).totalCost !== 0

    try {
      if (requireCreditCard) {
        await getSessionWithStripe(id)
      } else {
        await getNoCreditCardSession()
      }
    } catch (err) {
      showErrorNotification('Failed to create JAAS subscription')
      setContext({
        ...context,
        loading: false
      })
    }
  }

  const hideDialog = () => setDialog(null)

  const showDialog = dialogComponent => setDialog(dialogComponent)

  const setCoupon = coupon => {
    setContext({
      ...context,
      coupon
    })
  }

  useEffect(() => {
    init()
  }, [])

  return (
    <Context.Provider
      value={{
        ...context,
        getSession,
        hideDialog,
        setCoupon,
        showDialog
      }}
    >
      <>
        <RenderedApp />
        {dialog}
      </>
    </Context.Provider>
  )
}

App.propTypes = {
  publicKey: PropTypes.string.isRequired,
  planId: PropTypes.string
}

export default withNotifications(App)
